import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leaveRequestsService } from '../../services/api/leaveRequests';
import { LeaveRequestDetail as LeaveRequestDetailType, LeaveRequestStatus } from '../../types';
import StatusBadge from '../../components/common/StatusBadge';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import './LeaveRequestDetail.css';

const LeaveRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<LeaveRequestDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequest = async () => {
      if (!id) {
        setError('Invalid request ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await leaveRequestsService.getById(parseInt(id, 10));
        setRequest(data);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load leave request');
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [id]);

  if (loading) return <Loading text="Loading leave request details..." />;
  if (error) return <ErrorMessage error={error} onRetry={() => window.location.reload()} />;
  if (!request) return <ErrorMessage error="Leave request not found" />;

  return (
    <div className="leave-request-detail-page">
      <div className="detail-header">
        <button
          className="button button-secondary"
          onClick={() => navigate('/leave-requests/me')}
        >
          ← Back to My Requests
        </button>
        <h1>Leave Request Details</h1>
      </div>

      <div className="detail-card">
        <div className="detail-section">
          <div className="detail-row">
            <span className="detail-label">Status</span>
            <StatusBadge status={request.status} />
          </div>

          <div className="detail-row">
            <span className="detail-label">Employee</span>
            <span className="detail-value">{request.employee?.fullName || 'N/A'}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Employee Code</span>
            <span className="detail-value">{request.employee?.employeeCode || 'N/A'}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Leave Type</span>
            <span className="detail-value">{request.leaveType?.name || 'N/A'}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Start Date</span>
            <span className="detail-value">
              {new Date(request.startDate).toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">End Date</span>
            <span className="detail-value">
              {new Date(request.endDate).toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>

          {request.halfDayType && request.halfDayType !== 'NONE' && (
            <div className="detail-row">
              <span className="detail-label">Half Day Type</span>
              <span className="detail-value">
                {request.halfDayType === 'MORNING' ? 'Morning (08:30 - 12:00)' : 'Afternoon (13:30 - 18:00)'}
              </span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Total Days</span>
            <span className="detail-value">{request.totalDays} day(s)</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Reason</span>
            <span className="detail-value reason-text">{request.reason}</span>
          </div>

          {(request.isLateSubmission === true || request.isLateSubmission === 1) && (
            <div className="detail-row warning">
              <span className="detail-label">⚠️ Late Submission</span>
              <span className="detail-value">This request was submitted after the deadline</span>
            </div>
          )}

          {request.isPaid !== null && request.isPaid !== undefined && (
            <div className="detail-row">
              <span className="detail-label">Paid Status</span>
              <span className={`detail-value ${request.isPaid === true || request.isPaid === 1 ? 'paid' : 'unpaid'}`}>
                {request.isPaid === true || request.isPaid === 1 ? '✓ Paid Leave' : '✗ Unpaid Leave'}
              </span>
            </div>
          )}

          {request.submittedAt && (
            <div className="detail-row">
              <span className="detail-label">Submitted At</span>
              <span className="detail-value">
                {new Date(request.submittedAt).toLocaleString('en-GB')}
              </span>
            </div>
          )}

          {request.lineManager && (
            <div className="detail-row">
              <span className="detail-label">Line Manager</span>
              <span className="detail-value">{request.lineManager.email}</span>
            </div>
          )}

          {request.lineManagerApprovedAt && (
            <div className="detail-row">
              <span className="detail-label">Line Manager Approved At</span>
              <span className="detail-value">
                {new Date(request.lineManagerApprovedAt).toLocaleString('en-GB')}
              </span>
            </div>
          )}

          {request.skipManager && (
            <div className="detail-row">
              <span className="detail-label">Skip Manager</span>
              <span className="detail-value">{request.skipManager.email}</span>
            </div>
          )}

          {request.skipManagerApprovedAt && (
            <div className="detail-row">
              <span className="detail-label">Skip Manager Approved At</span>
              <span className="detail-value">
                {new Date(request.skipManagerApprovedAt).toLocaleString('en-GB')}
              </span>
            </div>
          )}

          {request.rejectionReason && (
            <div className="detail-row error">
              <span className="detail-label">Rejection Reason</span>
              <span className="detail-value">{request.rejectionReason}</span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Created At</span>
            <span className="detail-value">
              {new Date(request.createdAt).toLocaleString('en-GB')}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Last Updated</span>
            <span className="detail-value">
              {new Date(request.updatedAt).toLocaleString('en-GB')}
            </span>
          </div>
        </div>

        {request.status === LeaveRequestStatus.DRAFT && (
          <div className="detail-actions">
            <button
              className="button button-secondary"
              onClick={() => navigate(`/leave-requests/${request.id}/edit`)}
            >
              Edit Request
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveRequestDetail;
